import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { FileText, Plus, CheckCircle2, XCircle, Send, FileEdit, Trash2, X, CheckSquare, Square } from "lucide-react";

const STATUS = {
  draft: { label: "Borrador", cls: "bg-slate-100 text-slate-700", icon: FileEdit },
  sent: { label: "Enviado", cls: "bg-blue-100 text-blue-700", icon: Send },
  accepted: { label: "Aceptado", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
  rejected: { label: "Rechazado", cls: "bg-red-100 text-red-700", icon: XCircle },
};

const FILTERS = [
  { id: "all", label: "Todos" },
  { id: "draft", label: "Borradores" },
  { id: "sent", label: "Enviados" },
  { id: "accepted", label: "Aceptados" },
  { id: "rejected", label: "Rechazados" },
];

export default function Budgets() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/budgets").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = items
    .filter((b) => filter === "all" || b.status === filter)
    .filter((b) => !search || b.client_name?.toLowerCase().includes(search.toLowerCase()) || b.ai_data?.title?.toLowerCase().includes(search.toLowerCase()) || b.number?.toLowerCase().includes(search.toLowerCase()));

  const counts = items.reduce((acc, b) => { acc[b.status] = (acc[b.status] || 0) + 1; return acc; }, {});

  const toggleSelect = (id) => {
    setSelected((s) => {
      const ns = new Set(s);
      ns.has(id) ? ns.delete(id) : ns.add(id);
      return ns;
    });
  };

  const selectAllVisible = () => {
    if (selected.size === filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((b) => b.id)));
    }
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const deleteOne = async (id, number) => {
    if (!window.confirm(`¿Borrar el presupuesto ${number}?\n\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/budgets/${id}`);
      toast.success("Presupuesto borrado");
      load();
    } catch {
      toast.error("Error borrando el presupuesto");
    }
  };

  const deleteBulk = async () => {
    setBusy(true);
    let ok = 0, fail = 0;
    for (const id of Array.from(selected)) {
      try {
        await api.delete(`/budgets/${id}`);
        ok++;
      } catch { fail++; }
    }
    setBusy(false);
    setConfirmBulk(false);
    exitSelectMode();
    if (ok) toast.success(`${ok} presupuesto${ok === 1 ? "" : "s"} borrado${ok === 1 ? "" : "s"}`);
    if (fail) toast.error(`${fail} fallaron`);
    load();
  };

  return (
    <div data-testid="budgets-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kbar">Presupuestos</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Tus presupuestos</h1>
          <p className="text-slate-500 mt-2">{items.length} en total · {counts.accepted || 0} aceptados</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!selectMode ? (
            <>
              <button
                onClick={() => setSelectMode(true)}
                data-testid="btn-select-mode"
                className="h-12 px-4 border border-slate-200 hover:border-slate-400 inline-flex items-center gap-2 text-sm font-semibold"
              >
                <CheckSquare className="w-4 h-4" /> Seleccionar
              </button>
              <Link to="/generator" data-testid="budgets-new" className="btn-brand h-12 px-6 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Nuevo presupuesto</Link>
            </>
          ) : (
            <>
              <button
                onClick={selectAllVisible}
                data-testid="btn-select-all"
                className="h-12 px-4 border border-slate-200 hover:border-slate-400 inline-flex items-center gap-2 text-sm font-semibold"
              >
                {selected.size === filtered.length && filtered.length > 0 ? "Deseleccionar todos" : "Seleccionar todos"}
              </button>
              <button
                onClick={() => setConfirmBulk(true)}
                data-testid="btn-delete-bulk"
                disabled={selected.size === 0}
                className="h-12 px-5 bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-2 text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4" /> Borrar {selected.size > 0 ? `(${selected.size})` : ""}
              </button>
              <button
                onClick={exitSelectMode}
                data-testid="btn-cancel-select"
                className="h-12 px-4 border border-slate-200 hover:border-slate-400 inline-flex items-center gap-2 text-sm font-semibold"
              >
                <X className="w-4 h-4" /> Cancelar
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}
              className={`h-10 px-4 text-sm font-bold transition-colors border ${filter === f.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              {f.label}{f.id !== "all" && counts[f.id] ? ` · ${counts[f.id]}` : ""}
            </button>
          ))}
        </div>
        <input
          data-testid="budgets-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por cliente, título o nº…"
          className="h-10 px-3 border border-slate-200 focus:border-orange-600 outline-none w-full sm:w-72"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="mt-10 border border-slate-200 bg-white p-16 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto" />
          <p className="mt-4 text-slate-500">No hay presupuestos con este filtro.</p>
          <Link to="/generator" className="mt-5 inline-flex btn-brand h-11 px-5 items-center gap-2"><Plus className="w-4 h-4" /> Crear el primero</Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((b) => (
            <BudgetCard
              key={b.id}
              b={b}
              selectMode={selectMode}
              selected={selected.has(b.id)}
              onToggleSelect={() => toggleSelect(b.id)}
              onDelete={() => deleteOne(b.id, b.number)}
            />
          ))}
        </div>
      )}

      {confirmBulk && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !busy && setConfirmBulk(false)}>
          <div className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="bulk-confirm-modal">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-12 w-12 bg-red-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <div className="text-lg font-bold">¿Borrar {selected.size} presupuesto{selected.size === 1 ? "" : "s"}?</div>
                <div className="text-xs text-slate-500">Esta acción no se puede deshacer</div>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-4">Se borrarán definitivamente los presupuestos seleccionados y sus enlaces públicos dejarán de funcionar.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmBulk(false)} disabled={busy} className="px-4 py-2 text-sm border border-slate-200 hover:border-slate-400">
                Cancelar
              </button>
              <button
                onClick={deleteBulk}
                disabled={busy}
                data-testid="bulk-confirm-btn"
                className="px-4 py-2 bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? "Borrando…" : `Sí, borrar ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BudgetCard({ b, selectMode, selected, onToggleSelect, onDelete }) {
  const s = STATUS[b.status] || STATUS.draft;
  const Icon = s.icon;
  const total = b.ai_data?.total || 0;
  const taxLabel = b.ai_data?.iva_rate === 0 ? "Sin impuesto" : `${b.ai_data?.tax_label || "IVA"} ${b.ai_data?.iva_rate || 21}%`;
  const created = b.created_at?.slice(0, 10);

  const innerContent = (
    <>
      <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-start gap-2">
          {selectMode && (
            <div className="mt-1">
              {selected ? <CheckSquare className="w-5 h-5 text-orange-600" /> : <Square className="w-5 h-5 text-slate-400" />}
            </div>
          )}
          <div className="min-w-0">
            <div className="kbar truncate">{b.number}{b.project_reference ? ` · ${b.project_reference}` : ""}</div>
            <h3 className="font-display font-bold text-lg leading-snug mt-1 line-clamp-2 group-hover:text-orange-600 transition-colors">{b.ai_data?.title || b.work_type}</h3>
          </div>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1 ${s.cls} flex-shrink-0`}>
          <Icon className="w-3 h-3" /> {s.label}
        </span>
      </div>
      <div className="p-5 flex-1 space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Cliente</span>
          <span className="font-semibold truncate ml-2">{b.client_name}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Fecha</span>
          <span className="text-slate-700">{created}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">{taxLabel}</span>
          <span className="text-slate-700 tabular-nums">{(b.ai_data?.iva_amount || 0).toFixed(2)} €</span>
        </div>
        {b.signature && <div className="text-xs text-blue-700 font-bold">✍ Firmado por {b.signature.signer_name}</div>}
      </div>
      <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50 group-hover:bg-orange-50 transition-colors">
        <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Total</span>
        <span className="font-display font-extrabold text-2xl tabular-nums">{total.toFixed(2)} €</span>
      </div>
    </>
  );

  if (selectMode) {
    return (
      <button
        onClick={onToggleSelect}
        data-testid={`budget-card-${b.id}`}
        className={`group bg-white border ${selected ? "border-orange-600 ring-2 ring-orange-200" : "border-slate-200 hover:border-slate-300"} hover:shadow-sm transition-all flex flex-col text-left w-full`}
      >
        {innerContent}
      </button>
    );
  }

  return (
    <div className="group relative bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all flex flex-col" data-testid={`budget-card-${b.id}`}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
        data-testid={`budget-delete-${b.id}`}
        title="Borrar"
        aria-label="Borrar presupuesto"
        className="absolute top-3 right-3 z-10 h-8 w-8 bg-white/0 hover:bg-red-50 hover:text-red-600 text-slate-400 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center border border-transparent hover:border-red-200"
      >
        <Trash2 className="w-4 h-4" />
      </button>
      <Link to={`/budgets/${b.id}`} className="flex flex-col flex-1">
        {innerContent}
      </Link>
    </div>
  );
}
